import { Injectable } from '@angular/core';
import {
	ExternalTransferApiService,
	ExternalTransferOnlyStructureDto,
	ExternalTransferOnlyStructureResponseDto
} from '@api/external-transfer';
import { ErrorCodes, FileModel, ToastDuration, ToastService, lowercaseKeys } from '@shared';
import { firstValueFrom, map } from 'rxjs';
import { TransferOutStragegy } from './transfer-out.strategy';
import { RegistrationHandler } from '../../../../domain/json-file-importer/import-process/registration';
import { FileValidatorHandler } from '../../../../domain/json-file-importer/import-process/validation';
import { LoggingService } from '@shared/logging';
import { DeviceIdProvider } from '@database/workspace';
import { JsonFileDataParser } from '@database/central';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class StructureTransferOutStrategy extends TransferOutStragegy {
	public constructor(
		private externalTransferApiService: ExternalTransferApiService,
		private loggingService: LoggingService,
		private deviceIdProvider: DeviceIdProvider,
		private jsonFileDataParser: JsonFileDataParser,
		private toastService: ToastService,
		private translateService: TranslateService
	) {
		super();
	}

	public async validate(
		connectionId: string,
		environment: string,
		businessUnit: string,
		dhlLoggingStyle: string,
		sstnLoggingStyle: string
	): Promise<FileModel> {
		let workfile: FileModel;

		try {
			workfile = await firstValueFrom(
				this.externalTransferApiService
					.exportOnlyStructure(
						connectionId,
						new ExternalTransferOnlyStructureDto({
							Environment: environment,
							businessUnit,
							LoggingStyle: dhlLoggingStyle,
							sstnLoggingStyle
						})
					)
					.pipe(map((response: ExternalTransferOnlyStructureResponseDto) => JSON.parse(response.Workfile)))
			);
			workfile.TABLE_DATA = lowercaseKeys(workfile.TABLE_DATA)
		} catch (error) {
			this.loggingService.error(ErrorCodes.TransferOutStructureError, error.errorMessage);
			await this.toastService.presentError(error.errorMessage, ToastDuration.Fast);
			throw new Error(ErrorCodes.TransferOutStructureError);
		}

		// 1. registration handler
		const registrationHandler = new RegistrationHandler(this.loggingService, this.deviceIdProvider);

		// 2. validate file handler
		const fileValidator = new FileValidatorHandler(
			this.loggingService,
			this.jsonFileDataParser,
			this.toastService,
			this.translateService
		);

		registrationHandler.setNext(fileValidator);

		await registrationHandler.handle(workfile);

		return workfile;
	}
}
